import {
  AccessTime,
  Campaign,
  Check,
  Error,
  MoreVert,
  RefreshOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Company } from "../../types";
import { hostname, parseError } from "../../utils";
import ContentCampaignDialog from "./ContentCampaignDialog";

export default ({
  currentUser,
  getToken,
  openDialog,
  eventListener,
  companyId,
  company,
  currentPlan,
}: {
  getToken: Function;
  openDialog: Function;
  eventListener: Function;
  currentUser: any | User;
  companyId: string;
  company: Company;
  currentPlan: string;
}) => {
  const [contents, setContents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [pollingCampaigns, setPollingCampaigns] = useState<Set<string>>(
    new Set()
  );
  const pollingIntervals = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const pollingTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const [exportImportOpen, setExportImportOpen] = useState(false);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteContentCampaign = async (contentCampaignId: string) => {
    openDialog(
      "Delete Content Campaign",
      `Are you sure you want to delete this content Campaign?`,
      "Confirm",
      () => {
        setLoading(true);
        getToken().then((currentToken: string) => {
          return axios
            .delete(`${hostname}/content/campaigns/${contentCampaignId}`, {
              params: { companyId },
              headers: {
                Authorization: `Bearer ${currentToken}`,
              },
            })
            .catch((err) => {
              eventListener({
                type: "SET_ERROR",
                error: parseError(err),
              });
            })
            .finally(() => {
              setLoading(false);
              refreshContentCampaigns();
            });
        });
      }
    );
  };

  const refreshContentCampaigns = async () => {
    setRefreshing(true);
    const currentToken = await getToken();
    axios
      .get(`${hostname}/content/campaigns`, {
        params: { companyId, page: page + 1, pageSize },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setCampaigns(res.data.items);
        setTotal(res.data.total);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setRefreshing(false);
      });
  };

  const pollCampaignStatus = async (campaignId: string) => {
    try {
      const currentToken = await getToken();
      const response = await axios.get(`${hostname}/content/campaigns`, {
        params: { companyId, page: page + 1, pageSize },
        headers: { Authorization: `Bearer ${currentToken}` },
      });

      const updatedCampaigns = response.data.items;
      setCampaigns(updatedCampaigns);
      setTotal(response.data.total);

      // Check if the specific campaign is no longer running
      const campaign = updatedCampaigns.find((c: any) => c.id === campaignId);
      if (campaign && campaign.status !== "running") {
        stopPollingCampaign(campaignId);
      }
    } catch (error) {
      console.error("Error polling campaign status:", error);
    }
  };

  const stopPollingCampaign = (campaignId: string) => {
    // Clear interval
    const interval = pollingIntervals.current.get(campaignId);
    if (interval) {
      clearInterval(interval);
      pollingIntervals.current.delete(campaignId);
    }

    // Clear timeout
    const timeout = pollingTimeouts.current.get(campaignId);
    if (timeout) {
      clearTimeout(timeout);
      pollingTimeouts.current.delete(campaignId);
    }

    // Remove from polling set
    setPollingCampaigns((prev) => {
      const newSet = new Set(prev);
      newSet.delete(campaignId);
      return newSet;
    });
  };

  const startPollingCampaign = (campaignId: string) => {
    // Add to polling set
    setPollingCampaigns((prev) => new Set(prev).add(campaignId));

    // Set up polling interval (every 7 seconds)
    const interval = setInterval(() => {
      pollCampaignStatus(campaignId);
    }, 7000);
    pollingIntervals.current.set(campaignId, interval);

    // Set up 5-minute timeout
    const timeout = setTimeout(() => {
      stopPollingCampaign(campaignId);
    }, 5 * 60 * 1000); // 5 minutes
    pollingTimeouts.current.set(campaignId, timeout);
  };

  const triggerCampaign = async (campaignId: string) => {
    const currentToken = await getToken();
    await axios
      .post(
        `${hostname}/content/campaigns/${campaignId}/trigger`,
        { companyId },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      )
      .then(() => {
        refreshContentCampaigns();
        startPollingCampaign(campaignId);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const frequencyMapping = (frequency?: string | null) => {
    if (!frequency) {
      return "-";
    }
    switch (frequency) {
      case "daily":
        return "Daily";
      case "weekly":
        return "Weekly";
      case "monthly":
        return "Monthly";
      default:
        return frequency.charAt(0).toUpperCase() + frequency.slice(1);
    }
  };

  const loadData = async () => {
    setLoading(true);
    await refreshContentCampaigns();
    setLoading(false);
  };

  const getStatus = (status: string, updatedDate?: string) => {
    if (status === "running") {
      // Check if campaign has been running for more than 15 minutes
      const isStale =
        updatedDate &&
        moment().diff(moment(updatedDate), "minutes") > 15;

      if (isStale) {
        // Show as healthy for stale campaigns
        return <Chip icon={<Check />} label="Healthy" />;
      }
      return <Chip icon={<CircularProgress size={20} />} label="Running" />;
    } else if (status === "error") {
      return <Chip icon={<Error />} label="Error" />;
    } else {
      return <Chip icon={<Check />} label="Healthy" />;
    }
  };

  useEffect(() => {
    if (currentUser.uid) {
      refreshContentCampaigns();
    }
  }, [page, pageSize]);

  useEffect(() => {
    if (currentUser.uid) {
      loadData();
    }
  }, [currentUser.uid, companyId]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      // Clear all polling intervals and timeouts
      pollingIntervals.current.forEach((interval) => clearInterval(interval));
      pollingTimeouts.current.forEach((timeout) => clearTimeout(timeout));
      pollingIntervals.current.clear();
      pollingTimeouts.current.clear();
    };
  }, []);

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          sx={{ mt: 2 }}
          onClick={() => {
            setSelectedCampaign(null);
            setCampaignOpen(true);
          }}
          startIcon={<Campaign />}
          variant="contained"
        >
          New Campaign
        </Button>
      </Box>
      <Paper style={{ marginTop: 20, marginBottom: 40 }} variant="outlined">
        {refreshing && <LinearProgress />}
        {!refreshing && <div style={{ height: 4 }}></div>}
        <IconButton
          onClick={() => refreshContentCampaigns()}
          style={{ float: "right", marginRight: 5 }}
        >
          <RefreshOutlined />
        </IconButton>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Content Count</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((campaign: any) => (
                <TableRow key={campaign.id}>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        minHeight: 40,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: campaign.color,
                          minWidth: 7,
                          width: 7,
                          alignSelf: "stretch",
                          borderRadius: 3,
                          marginRight: 1,
                          flexShrink: 0,
                        }}
                      ></Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {campaign.name}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<AccessTime />}
                      label={frequencyMapping(campaign.frequency)}
                    />
                  </TableCell>
                  <TableCell>{getStatus(campaign.status, campaign.updated_date)}</TableCell>
                  <TableCell>
                    {campaign.created_date
                      ? moment(campaign.created_date).format(
                          "MMMM Do YYYY, h:mm:ss a"
                        )
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {campaign.updated_date
                      ? moment(campaign.updated_date).format(
                          "MMMM Do YYYY, h:mm:ss a"
                        )
                      : "-"}
                  </TableCell>
                  <TableCell>{campaign.content_count}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e: any) => {
                        e.stopPropagation();
                        setAnchorEl(e.currentTarget);
                        setSelectedId(campaign.id);
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={selectedId === campaign.id}
                      onClose={(e: any) => {
                        e.stopPropagation();
                        setAnchorEl(null);
                        setSelectedId(null);
                      }}
                    >
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setAnchorEl(null);
                          setSelectedId(null);
                          triggerCampaign(campaign.id);
                        }}
                      >
                        Trigger
                      </MenuItem>
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCampaign(campaign);
                          setCampaignOpen(true);
                          setAnchorEl(null);
                          setSelectedId(null);
                        }}
                      >
                        Edit
                      </MenuItem>
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteContentCampaign(campaign.id);
                          setAnchorEl(null);
                          setSelectedId(null);
                        }}
                      >
                        Delete
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>

      <ContentCampaignDialog
        open={campaignOpen}
        onClose={() => {
          setCampaignOpen(false);
          setSelectedCampaign(null);
          refreshContentCampaigns();
        }}
        selectedCampaign={selectedCampaign}
        companyId={companyId}
        company={company}
        getToken={getToken}
        eventListener={eventListener}
        currentPlan={currentPlan}
      />
    </Box>
  );
};
